/**
 * Saved selection sets service context.
 *
 * VITE_SELECTION_SETS_SERVICE controls the adapter:
 *   mock (default) — MockSelectionSetService; canned data, nothing persists
 *   http           — HttpSelectionSetService; calls /api/selection-sets/*
 */

import { createContext, useContext, type ReactNode } from 'react'
import type { ISelectionSetService } from './ISelectionSetService.interface'
import { MockSelectionSetService } from './MockSelectionSetService'
import { HttpSelectionSetService } from './HttpSelectionSetService'

function resolveService(): ISelectionSetService {
  const key = (import.meta.env.VITE_SELECTION_SETS_SERVICE as string | undefined) ?? 'mock'
  switch (key) {
    case 'http': return new HttpSelectionSetService()
    case 'mock':
    default:     return new MockSelectionSetService()
  }
}

const defaultService = resolveService()

const SelectionSetServiceContext = createContext<ISelectionSetService>(defaultService)

interface SelectionSetServiceProviderProps {
  children: ReactNode
  /** Override the service instance — used in tests. */
  service?: ISelectionSetService
}

export function SelectionSetServiceProvider({
  children,
  service: override,
}: SelectionSetServiceProviderProps) {
  return (
    <SelectionSetServiceContext.Provider value={override ?? defaultService}>
      {children}
    </SelectionSetServiceContext.Provider>
  )
}

export function useSelectionSetService(): ISelectionSetService {
  return useContext(SelectionSetServiceContext)
}
